import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as packagingApi from '../api/packagingApi';
import {
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  Modal,
  Skeleton,
  Spinner,
  TableSkeleton,
} from '../components/ui';
import { IconAlert, IconTrash } from '../components/icons';
import { PACKAGING_TYPES, PackagingItem, PackagingType } from '../types/packaging';
import { capitalize, formatDate, formatINRPrecise } from '../utils/format';

function StockStatus({ status }: { status: PackagingItem['status'] }) {
  if (status === 'out')
    return (
      <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-0.5 text-xs font-medium text-red-600 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-300">
        OUT
      </span>
    );
  if (status === 'low')
    return (
      <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300">
        LOW
      </span>
    );
  return (
    <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300">
      OK
    </span>
  );
}

const BLANK_NEW = {
  item_name: '',
  item_type: 'box' as PackagingType,
  quantity: '',
  unit_cost: '',
  reorder_level: '10',
  notes: '',
};

export default function PackagingPage() {
  const [items, setItems] = useState<PackagingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  // Restock modal
  const [restockItem, setRestockItem] = useState<PackagingItem | null>(null);
  const [restockQty, setRestockQty] = useState('');
  const [restockCost, setRestockCost] = useState('');
  const [restocking, setRestocking] = useState(false);

  // Add-item modal
  const [addOpen, setAddOpen] = useState(false);
  const [newItem, setNewItem] = useState(BLANK_NEW);
  const [creating, setCreating] = useState(false);

  // Edit modal — name / type / reorder level / notes. Stock and cost are
  // deliberately not editable here; they move through Restock.
  const [editItem, setEditItem] = useState<PackagingItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<PackagingType | ''>('');
  const [editLevel, setEditLevel] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation. Every parcel consumes one of each item, so removing one
  // changes what future parcels cost — worth a confirm even though past orders
  // keep the packaging cost they were priced at.
  const [deleteTarget, setDeleteTarget] = useState<PackagingItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await packagingApi.getPackaging());
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRestock = async () => {
    if (!restockItem) return;
    const qty = Number(restockQty);
    if (!qty || qty <= 0) {
      setActionError('Enter a quantity greater than 0');
      return;
    }
    const costGiven = restockCost.trim() !== '';
    const cost = Number(restockCost);
    if (costGiven && (!Number.isFinite(cost) || cost < 0)) {
      setActionError('Enter a unit cost of 0 or more');
      return;
    }
    setRestocking(true);
    setActionError('');
    try {
      await packagingApi.restockPackaging(restockItem.id, qty, costGiven ? cost : undefined);
      setRestockItem(null);
      setRestockQty('');
      setRestockCost('');
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setRestocking(false);
    }
  };

  const handleCreate = async () => {
    const name = newItem.item_name.trim();
    if (!name) {
      setActionError('Enter an item name');
      return;
    }
    setCreating(true);
    setActionError('');
    try {
      await packagingApi.createPackagingItem({
        item_name: name,
        item_type: newItem.item_type,
        quantity: Number(newItem.quantity) || 0,
        unit_cost: Number(newItem.unit_cost) || 0,
        reorder_level: Number(newItem.reorder_level) || 0,
        notes: newItem.notes.trim(),
      });
      setAddOpen(false);
      setNewItem(BLANK_NEW);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    const name = editName.trim();
    if (!name) {
      setActionError('Item name cannot be empty');
      return;
    }
    const level = Number(editLevel);
    if (editLevel.trim() === '' || !Number.isFinite(level) || level < 0) {
      setActionError('Enter a reorder level of 0 or more');
      return;
    }
    setSaving(true);
    setActionError('');
    try {
      await packagingApi.updatePackagingItem(editItem.id, {
        item_name: name,
        item_type: editType === '' ? null : editType,
        reorder_level: level,
        notes: editNotes.trim(),
      });
      setEditItem(null);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError('');
    try {
      await packagingApi.deletePackagingItem(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (item: PackagingItem) => {
    setEditItem(item);
    setEditName(item.item_name);
    setEditType(item.item_type ?? '');
    setEditLevel(String(item.reorder_level));
    setEditNotes(item.notes ?? '');
  };

  // "Below reorder level" covers both LOW and OUT — an item at zero is the most
  // below its threshold it can possibly be, and excluding it would hide the
  // urgent case from the number the admin scans first.
  const belowReorder = items.filter((i) => i.status !== 'ok');
  const outOfStock = items.filter((i) => i.status === 'out');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Packaging</h1>
          <p className="text-sm text-sub">
            Boxes, jackets, cards and wraps — one complete set is consumed per shipment
          </p>
        </div>
        <button className="btn-gold" onClick={() => { setNewItem(BLANK_NEW); setAddOpen(true); }}>
          Add new item
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {actionError && <ErrorBanner message={actionError} />}

      {/* Out of stock is not a blocker — shipments still go out, they just stop
          drawing down the count — but it means the number on this page has
          drifted from the shelf, so it needs saying loudly. */}
      {outOfStock.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-bad/40 bg-bad/10 px-4 py-3 text-sm text-bad">
          <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            <span className="font-semibold">
              {outOfStock.map((i) => i.item_name).join(', ')} at zero.
            </span>{' '}
            Shipments are not blocked, but these are no longer being deducted — restock or correct
            the count.
          </p>
        </div>
      )}

      {/* Summary row */}
      {loading ? (
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Packaging types</p>
            <p className="mt-2 text-2xl font-semibold">{items.length}</p>
          </div>
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">
              Below reorder level
            </p>
            <p
              className={`mt-2 text-2xl font-semibold ${
                belowReorder.length === 0 ? 'text-ok' : 'text-warn'
              }`}
            >
              {belowReorder.length}
            </p>
          </div>
        </div>
      )}

      <Card bodyClassName="!p-0">
        {loading ? (
          <TableSkeleton rows={5} />
        ) : items.length === 0 ? (
          <EmptyState
            message="No packaging items"
            hint="Add the boxes, jackets and cards that go into every parcel."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">Item name</th>
                  <th className="th">Type</th>
                  <th className="th text-right">In stock</th>
                  <th className="th text-right">Reorder level</th>
                  <th className="th text-right">Unit cost</th>
                  <th className="th">Last restocked</th>
                  <th className="th">Status</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {items.map((item) => (
                  <tr key={item.id} className="transition-colors hover:bg-edge/20">
                    <td className="td font-medium">
                      {item.item_name}
                      {item.notes && (
                        <span
                          className="block max-w-[240px] truncate text-xs text-sub"
                          title={item.notes}
                        >
                          {item.notes}
                        </span>
                      )}
                    </td>
                    <td className="td text-sub">
                      {item.item_type ? capitalize(item.item_type) : '—'}
                    </td>
                    <td className="td text-right font-medium">{item.quantity}</td>
                    <td className="td text-right text-sub">{item.reorder_level}</td>
                    <td className="td text-right">
                      {item.unit_cost ? (
                        formatINRPrecise(item.unit_cost)
                      ) : (
                        <span className="text-sub">—</span>
                      )}
                    </td>
                    <td className="td text-sub">{formatDate(item.last_restocked_at)}</td>
                    <td className="td">
                      <StockStatus status={item.status} />
                    </td>
                    <td className="td">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="btn-outline !px-3 !py-1.5 !text-xs"
                          onClick={() => {
                            setRestockItem(item);
                            setRestockQty('');
                            setRestockCost(item.unit_cost != null ? String(item.unit_cost) : '');
                          }}
                        >
                          Restock
                        </button>
                        <button
                          className="btn-outline !px-3 !py-1.5 !text-xs"
                          onClick={() => openEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-outline !px-2 !py-1.5 !text-xs text-red-600 hover:border-red-500/40 hover:bg-red-500/10 dark:text-red-300"
                          onClick={() => setDeleteTarget(item)}
                          title={`Delete ${item.item_name}`}
                          aria-label={`Delete ${item.item_name}`}
                        >
                          <IconTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Restock modal */}
      <Modal
        open={restockItem !== null}
        title="Restock packaging"
        onClose={() => setRestockItem(null)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setRestockItem(null)} disabled={restocking}>
              Cancel
            </button>
            <button
              className="btn-gold"
              onClick={handleRestock}
              disabled={restocking || !restockQty || Number(restockQty) <= 0}
            >
              {restocking && <Spinner />}
              Confirm restock
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Item</label>
            <input className="input opacity-70" value={restockItem?.item_name ?? ''} readOnly />
          </div>
          <div>
            <label className="label">Quantity to add (units)</label>
            <input
              className="input"
              type="number"
              min={1}
              step={1}
              placeholder="e.g. 200"
              value={restockQty}
              onChange={(e) => setRestockQty(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Unit cost (₹)</label>
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              placeholder="e.g. 35.00"
              value={restockCost}
              onChange={(e) => setRestockCost(e.target.value)}
            />
            <p className="mt-1.5 text-xs text-sub">
              What each unit cost in this batch. Replaces the recorded cost and is what order
              landed-cost uses. Leave as-is to keep the current price.
            </p>
          </div>
        </div>
      </Modal>

      {/* Add-item modal */}
      <Modal
        open={addOpen}
        title="Add packaging item"
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setAddOpen(false)} disabled={creating}>
              Cancel
            </button>
            <button
              className="btn-gold"
              onClick={handleCreate}
              disabled={creating || !newItem.item_name.trim()}
            >
              {creating && <Spinner />}
              Add item
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Item name</label>
            <input
              className="input"
              placeholder="e.g. Satin Ribbon"
              value={newItem.item_name}
              onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Type</label>
            <select
              className="input"
              value={newItem.item_type}
              onChange={(e) => setNewItem({ ...newItem, item_type: e.target.value as PackagingType })}
            >
              {PACKAGING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {capitalize(t)}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Opening stock</label>
              <input
                className="input"
                type="number"
                min={0}
                step={1}
                placeholder="0"
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Reorder level</label>
              <input
                className="input"
                type="number"
                min={0}
                step={1}
                placeholder="10"
                value={newItem.reorder_level}
                onChange={(e) => setNewItem({ ...newItem, reorder_level: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="label">Unit cost (₹)</label>
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              placeholder="e.g. 35.00"
              value={newItem.unit_cost}
              onChange={(e) => setNewItem({ ...newItem, unit_cost: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Notes (optional)</label>
            <textarea
              className="input min-h-[64px]"
              placeholder="Supplier, size, SKU…"
              value={newItem.notes}
              onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
            />
          </div>
          {/* Every item on this list is deducted from on every shipment, so
              adding one changes what a parcel costs — worth saying up front. */}
          <p className="text-xs text-sub">
            This item joins the standard set: one unit is deducted for every shipment created.
          </p>
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal
        open={editItem !== null}
        title="Edit packaging item"
        onClose={() => setEditItem(null)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setEditItem(null)} disabled={saving}>
              Cancel
            </button>
            <button className="btn-gold" onClick={handleSaveEdit} disabled={saving || !editName.trim()}>
              {saving && <Spinner />}
              Save changes
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Item name</label>
            <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
          </div>
          <div>
            <label className="label">Type</label>
            <select
              className="input"
              value={editType}
              onChange={(e) => setEditType(e.target.value as PackagingType | '')}
            >
              <option value="">—</option>
              {PACKAGING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {capitalize(t)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Reorder level (units)</label>
            <input
              className="input"
              type="number"
              min={0}
              step={1}
              value={editLevel}
              onChange={(e) => setEditLevel(e.target.value)}
            />
            <p className="mt-1.5 text-xs text-sub">
              Stock at or below this shows LOW and logs a warning after each shipment.
            </p>
          </div>
          <div>
            <label className="label">Notes (optional)</label>
            <textarea
              className="input min-h-[64px]"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
            />
          </div>
          <p className="text-xs text-sub">
            Stock and unit cost are not edited here — use Restock, which also records the date.
          </p>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete packaging item?"
        message={
          <>
            Delete <span className="font-semibold text-ink">{deleteTarget?.item_name}</span>? This
            cannot be undone.
            {deleteTarget != null && deleteTarget.quantity > 0 && (
              <span className="mt-2 block text-red-600 dark:text-red-300">
                It still holds {deleteTarget.quantity} unit(s) of stock — that count will be lost.
              </span>
            )}
            <span className="mt-2 block">
              Every parcel uses one of each item, so this also removes it from future parcels' cost.
              Past orders keep the packaging cost they were priced at.
            </span>
          </>
        }
        confirmLabel="Delete"
        danger
        busy={deleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
